import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";

import { useDraggable } from "hooks";
import { getRoutePointLocationFromMapBoxFeature } from "utils";

import { RoutePoint } from "./RoutePoint";

import type { SearchBoxFeatureSuggestion } from "@mapbox/search-js-core";

interface Props {
  routePoint: EditableRoutePointState;
  index: number;
  move: (dragIndex: number, hoverIndex: number) => void;
  onDeleteMidpoint: (index: number) => void;
  onSetRoutePointBeingEdited: (index: number, edit: boolean) => void;
  handleRoutePointValueChange: <T>(
    key: keyof EditableRoutePointState,
    value: T | null,
    index: number,
  ) => void;
  handleRoutePointLocationValueChange: <T>(
    key: keyof RoutePointLocation,
    value: T | null,
    index: number,
  ) => void;
  updateInputModeForRoutePoint: (
    index: number,
    mode: "autocomplete" | "manual",
  ) => void;
  // False when the point must carry a date (see EditableRoutePoint).
  allowFlexible?: boolean;
}

export function RoutePointContainer(props: Props) {
  const {
    routePoint,
    index,
    move,
    onDeleteMidpoint,
    onSetRoutePointBeingEdited,
    handleRoutePointValueChange,
    handleRoutePointLocationValueChange,
    updateInputModeForRoutePoint,
    allowFlexible = true,
  } = props;
  const { ref, handlerId, opacity } = useDraggable<EditableRoutePointState>(
    "RoutePoint",
    index,
    routePoint,
    move,
  );

  const isOriginOrDest = ["ORIGIN", "DESTINATION"].includes(
    routePoint?.stop_type ?? "",
  );

  const onRoutePointTimeChange = (key: "start_time", value: string) => {
    handleRoutePointValueChange(key, value, index);
  };

  const onAddressSuggestionSelected = (feature: SearchBoxFeatureSuggestion) => {
    const location = getRoutePointLocationFromMapBoxFeature(feature);
    handleRoutePointValueChange("point_location", location, index);
  };

  const onAddressCleared = () => {
    handleRoutePointValueChange("point_location", {}, index);
  };

  const handleScheduleTypeUpdated = (value: RoutePointScheduleType) => {
    handleRoutePointValueChange("schedule_type", value, index);
  };

  const routePointComponent = (
    <RoutePoint
      index={index}
      routePoint={routePoint}
      onRoutePointTimeChange={onRoutePointTimeChange}
      onAddressSuggestionSelected={onAddressSuggestionSelected}
      onAddressCleared={onAddressCleared}
      handleRoutePointLocationValueChange={handleRoutePointLocationValueChange}
      updateInputModeForRoutePoint={updateInputModeForRoutePoint}
      onSetRoutePointBeingEdited={onSetRoutePointBeingEdited}
      onScheduleTypeUpdate={handleScheduleTypeUpdated}
      allowFlexible={allowFlexible}
      deleteMidRoutePointButton={
        <Tooltip title="Delete midpoint">
          <IconButton onClick={() => onDeleteMidpoint(index)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      }
    />
  );

  if (isOriginOrDest) {
    return <div style={{ cursor: "default" }}>{routePointComponent}</div>;
  }

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      style={{ opacity, cursor: "move" }}
    >
      {routePointComponent}
    </div>
  );
}
